/**
 * Filter Bar Component
 * Provides search, filtering, and sorting controls for networks
 */

import React, { useState, useEffect } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { useNetworksStore } from '../../stores/networksStore';
import { useNetworkPlatformProviders } from '../../hooks/useNetworksData';
import * as Select from '@radix-ui/react-select';
import { ChevronDown } from 'lucide-react';

interface FilterBarProps {
  totalNetworks: number;
  filteredNetworks: number;
}

export const FilterBar: React.FC<FilterBarProps> = ({ totalNetworks, filteredNetworks }) => {
  const { filters, setFilters, resetFilters } = useNetworksStore();
  const { data: platforms } = useNetworkPlatformProviders();
  const [searchInput, setSearchInput] = useState(filters.search);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters({ search: searchInput });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, setFilters]);

  const hasActiveFilters = 
    filters.search !== '' || 
    filters.status !== 'all' || 
    filters.platform !== 'all' ||
    filters.sortBy !== 'last_sync' ||
    filters.sortOrder !== 'desc';

  return (
    <div className="bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Input */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
          <input
            type="text"
            placeholder="Search networks..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            aria-label="Search networks"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Status Filter */}
        <Select.Root value={filters.status} onValueChange={(value) => setFilters({ status: value as any })}>
          <Select.Trigger className="inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 min-w-[150px]">
            <span className="text-sm font-medium">Status: <Select.Value /></span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <Select.Viewport className="p-1">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="idle">Never Synced</SelectItem>
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        {/* Platform Filter */}
        <Select.Root value={filters.platform} onValueChange={(value) => setFilters({ platform: value })}>
          <Select.Trigger className="inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 min-w-[180px]">
            <span className="text-sm font-medium">Platform: <Select.Value /></span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <Select.Viewport className="p-1">
                <SelectItem value="all">All Platforms</SelectItem>
                {platforms?.map((platform) => (
                  <SelectItem key={platform.id} value={platform.id}>
                    {platform.name} ({platform.count})
                  </SelectItem>
                ))}
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        {/* Sort By */}
        <Select.Root value={filters.sortBy} onValueChange={(value) => setFilters({ sortBy: value as any })}>
          <Select.Trigger className="inline-flex items-center justify-between gap-2 px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 min-w-[150px]">
            <span className="text-sm font-medium">Sort: <Select.Value /></span>
            <ChevronDown className="w-4 h-4 text-gray-500" />
          </Select.Trigger>
          <Select.Portal>
            <Select.Content className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50">
              <Select.Viewport className="p-1">
                <SelectItem value="name">Name</SelectItem>
                <SelectItem value="balance">Balance</SelectItem>
                <SelectItem value="last_sync">Last Sync</SelectItem>
                <SelectItem value="status">Status</SelectItem>
              </Select.Viewport>
            </Select.Content>
          </Select.Portal>
        </Select.Root>

        {/* Sort Order Toggle */}
        <button
          onClick={() => setFilters({ sortOrder: filters.sortOrder === 'asc' ? 'desc' : 'asc' })}
          className="px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          aria-label={`Sort ${filters.sortOrder === 'asc' ? 'descending' : 'ascending'}`}
        >
          <span className="text-sm font-medium">
            {filters.sortOrder === 'asc' ? '↑ ASC' : '↓ DESC'}
          </span>
        </button>

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <button
            onClick={resetFilters}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500"
            aria-label="Reset all filters"
          >
            <X className="w-4 h-4" />
            Reset
          </button>
        )}
      </div>

      {/* Results count */}
      <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-700">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{filteredNetworks}</span> of{' '}
          <span className="font-semibold text-gray-900 dark:text-gray-100">{totalNetworks}</span> networks
        </p>
      </div>
    </div>
  );
};

// Select Item Component
interface SelectItemProps {
  value: string;
  children: React.ReactNode;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, ...props }, forwardedRef) => {
    return (
      <Select.Item
        className="relative flex items-center px-3 py-2 text-sm text-gray-900 dark:text-gray-100 rounded cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/30 focus:bg-blue-50 dark:focus:bg-blue-900/30 outline-none select-none data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/30 data-[highlighted]:text-blue-900 dark:data-[highlighted]:text-blue-100"
        {...props}
        ref={forwardedRef}
      >
        <Select.ItemText>{children}</Select.ItemText>
      </Select.Item>
    );
  }
);

SelectItem.displayName = 'SelectItem';
